import React, { useState, useEffect } from "react";
import "./../../css/dashboard.css";
import TodayCard from "../../components/TodayCard";
import { apiCall } from "../../services/authServieces"; 
import Loader from "../../components/Loader";
import { FiClock, FiCalendar, FiBell, FiUsers, FiRefreshCw } from "react-icons/fi";

// Enhanced Bar Chart Component
const EnhancedBarChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="chart-no-data">No data available</div>;
  }
  
  return (
    <div className="enhanced-bar-chart">
      {data.map((item, index) => (
        <div key={index} className="enhanced-bar-item">
          <div className="enhanced-bar-info">
            <div className="enhanced-bar-label">
              <span className="color-dot" style={{ backgroundColor: item.color }}></span>
              {item.name}
            </div>
            <div className="enhanced-bar-value">{item.value}</div>
          </div>
          <div className="enhanced-bar-container">
            <div 
              className="enhanced-bar-fill" 
              style={{ 
                width: `${Math.min(item.value / Math.max(...data.map(d => d.value)) * 100, 100)}%`,
                backgroundColor: item.color 
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Enhanced Modern Donut Chart
const EnhancedDonutChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="chart-no-data">No data available</div>;
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  
  // Calculate the SVG paths for the donut segments
  const segments = data.map((item, index) => {
    const percentage = total > 0 ? item.value / total : 0;
    const angle = percentage * 360;
    
    // Calculate SVG arc path
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    const startRadians = (startAngle - 90) * Math.PI / 180;
    const endRadians = (endAngle - 90) * Math.PI / 180;
    
    const x1 = 50 + 40 * Math.cos(startRadians);
    const y1 = 50 + 40 * Math.sin(startRadians);
    const x2 = 50 + 40 * Math.cos(endRadians);
    const y2 = 50 + 40 * Math.sin(endRadians);
    
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    const pathData = [
      `M 50 50`,
      `L ${x1} ${y1}`,
      `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `Z`
    ].join(' ');
    
    return { pathData, color: item.color, animationDelay: index * 0.1 };
  });
  
  return (
    <div className="enhanced-donut-container">
      <div className="enhanced-donut-chart">
        <svg width="200" height="200" viewBox="0 0 100 100">
          {segments.map((segment, index) => (
            <path
              key={index}
              d={segment.pathData}
              fill={segment.color}
              className="donut-segment"
              style={{ animationDelay: `${segment.animationDelay}s` }}
            />
          ))}
          <circle cx="50" cy="50" r="25" fill="white" className="donut-hole"/>
          <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" className="donut-total">
            {total}
          </text>
          <text x="50" y="58" textAnchor="middle" dominantBaseline="middle" className="donut-label">
            Total
          </text>
        </svg>
      </div>
      <div className="enhanced-donut-stats">
        {data.map((item, index) => {
          const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0';
          return (
            <div key={index} className="donut-stat-item" style={{ borderLeftColor: item.color }}>
              <div className="donut-stat-label">{item.name}</div>
              <div className="donut-stat-values">
                <span className="donut-stat-value">{item.value}</span>
                {/* <span className="donut-stat-percent">{percentage}%</span> */}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  // State to hold count data
  const [dashboardData, setDashboardData] = useState({
    sentToday: 0,
    sentThisWeek: 0,
    sentThisMonth: 0,
    pendingSms: 0,
  });
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // State for chart data
  const [barChartData, setBarChartData] = useState([]);
  const [donutChartData, setDonutChartData] = useState([]);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const response = await apiCall({
        endpoint: `admin/Dahboard_data_admin`,
        method: "GET",
      });
      
      if (response && response.data) {
        const data = response.data[0];
        
        // Set dashboard stats
        setDashboardData({
          sentToday: data.today_video_send_count || 0,
          sentThisWeek: data.week_video_send_count || 0,
          sentThisMonth: data.month_video_send_count || 0,
          pendingSms: data.last_15_days_video_send_count || 0,
        });

        // Create chart data
        const todayCount = data.today_video_send_count || 0;
        const weekCount = data.week_video_send_count || 0;
        const fifteenDaysCount = data.last_15_days_video_send_count || 0;
        const monthCount = data.month_video_send_count || 0;

        // Set bar chart data
        setBarChartData([
          { name: 'Today', value: todayCount, color: '#4F46E5' },
          { name: 'This Week', value: weekCount, color: '#10B981' },
          { name: '15 Days', value: fifteenDaysCount, color: '#F59E0B' },
          { name: 'This Month', value: monthCount, color: '#8B5CF6' }
        ]);

        // Set donut chart data - show distribution by period
        setDonutChartData([
          { name: 'Today', value: todayCount, color: '#4F46E5' },
          { name: 'Rest of Week', value: Math.max(weekCount - todayCount, 0), color: '#10B981' },
          { name: 'Rest of 15 Days', value: Math.max(fifteenDaysCount - weekCount, 0), color: '#F59E0B' },
          { name: 'Rest of Month', value: Math.max(monthCount, 0), color: '#8B5CF6' }
        ]);
      }
    } catch (error) {
      console.error("Error fetching dashboard data", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch data on component load
  useEffect(() => {
    fetchDashboardData();
  }, []); 

  return (
    <>
      {loading ? (
        <Loader /> 
      ) : (
        <div className="admin-dashboard">
          <div className="dashboard-header">
            <h1 className="dashboard-title">Admin Dashboard</h1>
            <button 
              className={`refresh-button ${refreshing ? 'refreshing' : ''}`} 
              onClick={fetchDashboardData}
              disabled={refreshing}
            >
              <FiRefreshCw size={16} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            {/* Today's Stats */}
            <div className="stat-card stat-today">
              <div className="stat-icon">
                <FiClock size={24} />
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{dashboardData.sentToday}</h3>
                <p className="stat-label">WhatsApp Sent Today</p>
              </div>
            </div>

            {/* Weekly Stats */}
            <div className="stat-card stat-week">
              <div className="stat-icon">
                <FiCalendar size={24} />
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{dashboardData.sentThisWeek}</h3>
                <p className="stat-label">WhatsApp Sent This Week</p>
              </div>
            </div>

            {/* 15 Days Stats */}
            <div className="stat-card stat-pending">
              <div className="stat-icon">
                <FiBell size={24} />
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{dashboardData.pendingSms}</h3>
                <p className="stat-label">WhatsApp Sent Last 15 Days</p>
              </div>
            </div>

            {/* Monthly Stats */}
            <div className="stat-card stat-month">
              <div className="stat-icon">
                <FiUsers size={24} />
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{dashboardData.sentThisMonth}</h3>
                <p className="stat-label">WhatsApp Sent This Month</p>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-grid">
            {/* Bar Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">Message Activity Summary</h3>
              </div>
              <EnhancedBarChart data={barChartData} />
            </div>

            {/* Donut Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">Message Distribution</h3>
              </div>
              <EnhancedDonutChart data={donutChartData} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;